<template>
  <div data-slot="magic-tool-generated-image-renderer">
    <canvas v-show="isImagesLoaded" ref="canvas" class="block max-w-full" />
    <div v-if="!isImagesLoaded" class="flex min-h-75 w-full items-center justify-center gap-2 text-sm text-muted-foreground">
      <loader-circle-icon class="size-5 animate-spin text-primary" />
      {{ tr('Loading...') }}
    </div>
  </div>
</template>

<script>
// Rendering runs off-screen and has no UI for failures: errors go to the console.
/* eslint-disable no-console */
import { LoaderCircle } from '@lucide/vue';
import emojiData from 'emoji-mart-vue-fast/data/facebook.json';
import emojiRegexBuilder from 'emoji-regex';

const emojiRegex = emojiRegexBuilder();
const emojiSizeMultiplier = 1.3;

const AI_WATERMARK_METADATA_MARKER = 'AI-generated content — marked under EU AI Act Article 50 (Regulation (EU) 2024/1689)';

const AI_WATERMARK_FALLBACK_LANGUAGE = 'en';
const AI_WATERMARK_OPACITY = 0.6;
const AI_WATERMARK_FONT_SIZE_RATIO = 0.02; // of the image's shorter side
const AI_WATERMARK_MIN_FONT_SIZE = 14;
const AI_WATERMARK_PADDING_RATIO = 1; // margin from the image edges
const AI_WATERMARK_GAP_RATIO = 0.5; // space between the icon and the text
const AI_WATERMARK_ICON_HEIGHT_RATIO = 1.2; // icon height vs. font size
const AI_WATERMARK_SHADOW_OFFSET_RATIO = 0.04;
const AI_WATERMARK_SHADOW_OPACITY_FACTOR = 0.55;

const AI_WATERMARK_LABELS = {
  bg: 'Съдържание, генерирано от ИИ',
  hr: 'Sadržaj generiran umjetnom inteligencijom',
  cs: 'Obsah vytvořený umělou inteligencí',
  da: 'AI-genereret indhold',
  nl: 'Door AI gegenereerde inhoud',
  en: 'AI-generated content',
  et: 'Tehisintellekti loodud sisu',
  fi: 'Tekoälyn luoma sisältö',
  fr: 'Contenu généré par IA',
  de: 'Von KI generierter Inhalt',
  el: 'Περιεχόμενο που δημιουργήθηκε από τεχνητή νοημοσύνη',
  hu: 'AI által előállított tartalom',
  ga: 'Ábhar a ghin intleacht shaorga',
  it: 'Contenuto generato dall\'IA',
  lv: 'Mākslīgā intelekta ģenerēts saturs',
  lt: 'Dirbtinio intelekto sukurtas turinys',
  mt: 'Kontenut iġġenerat mill-intelliġenza artifiċjali',
  pl: 'Treść wygenerowana przez sztuczną inteligencję',
  pt: 'Conteúdo gerado por IA',
  ro: 'Conținut generat de inteligența artificială',
  sk: 'Obsah vytvorený umelou inteligenciou',
  sl: 'Vsebina, ki jo je ustvarila umetna inteligenca',
  es: 'Contenido generado por IA',
  sv: 'AI-genererat innehåll',
  is: 'Efni búið til af gervigreind',
  no: 'KI-generert innhold',
  tr: 'Yapay zekâ tarafından oluşturulan içerik',
  sq: 'Përmbajtje e krijuar nga inteligjenca artificiale',
  bs: 'Sadržaj koji je generirala umjetna inteligencija',
  ru: 'Контент, созданный ИИ',
  uk: 'Контент, створений ШІ',
  sr: 'Садржај који је генерисала вештачка интелигенција',
  mk: 'Содржина генерирана од вештачка интелигенција',
};

const PNG_SIGNATURE = [ 0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A ];

export default {
  name: 'MagicToolGeneratedImageRenderer',
  components: {
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    imageUrl: {
      type: String,
      required: true,
    },
    logoUrl: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      required: false,
      default: () => '',
    },
    fontFamily: {
      type: String,
      required: false,
      default: () => 'Inter',
    },
    fontWeight: {
      type: [ String, Number ],
      required: false,
      default: () => '900',
    },
    logoSize: {
      type: [ String, Number ],
      required: false,
      default: () => 100,
    },
    watermark: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    aspectRatio: {
      type: String,
      required: false,
      default: () => '1:1',
    },
    language: {
      type: String,
      required: false,
      default: () => '',
    },
  },
  data: () => ({
    canvas: null,
    ctx: null,
    canvasWidth: 800,
    cachedMainImage: null,
    cachedWatermarkImage: null,
    cachedLogo: null,
    availableWeights: [],
    emojiExtractor: new EmojiExtractor(),
  }),
  computed: {
    calculatedAspectRatio() {
      const [ width, height ] = this.aspectRatio.split(':').map(Number);
      return width / height;
    },
    canvasHeight() {
      return this.canvasWidth / this.calculatedAspectRatio;
    },
    fontsLoaded() {
      if (process.env.VUE_APP_IS_DEMO) {
        return true;
      }

      return this.$store.getters['creatorLite/fontsLoaded'];
    },
    imageRatio() {
      if (!this.cachedMainImage) {
        return 1;
      }

      return this.cachedMainImage.width / this.cachedMainImage.height;
    },
    bottomOffset() {
      if (this.imageRatio === 1) {
        return 63; // 1:1
      }
      if (this.imageRatio >= 0.7) {
        return 140; // 3:4
      }
      if (this.imageRatio >= 0.5) {
        return 268; // 9:16
      }

      return 63; // Default fallback
    },
    gradientRatio() {
      if (this.imageRatio === 1) {
        return 0.5;
      }
      if (this.imageRatio >= 0.7) {
        return 0.7; // 3:4
      }
      else if (this.imageRatio >= 0.5) {
        return 0.9; // 9:16
      }

      return 0.5; // Default fallback
    },
    gradientHeight() {
      return this.canvasWidth * this.gradientRatio;
    },
    logoHeight() {
      return this.canvasWidth * 0.08 * (this.logoSize / 100);
    },
    logoYPosition() {
      // Removed dynamic calculation:
      // return this.canvasHeight - (this.canvasWidth * 0.07) - this.logoHeight;

      return this.canvasHeight - this.logoHeight - this.bottomOffset;
    },
    isImagesLoaded() {
      return this.cachedMainImage;
    },
    aiWatermarkLabel() {
      const primary = (this.language || '').trim().toLowerCase().split(/[-_]/)[0];
      const iso = (/^[a-z]{2}$/).test(primary) ? primary : AI_WATERMARK_FALLBACK_LANGUAGE;

      return AI_WATERMARK_LABELS[iso] || AI_WATERMARK_LABELS[AI_WATERMARK_FALLBACK_LANGUAGE];
    },
  },
  watch: {
    fontsLoaded() {
      this.quickRender();
    },
    fontFamily() {
      this.quickRender();
    },
    fontWeight() {
      this.quickRender();
    },
    imageUrl(newUrl) {
      if (newUrl) {
        this.cachedMainImage = null;
        this.fullRender();
      }
    },
    logoUrl() {
      this.cachedLogo = null;
      this.fullRender();
    },
    text() {
      this.quickRender();
    },
    logoSize() {
      this.quickRender();
    },
    language() {
      this.quickRender();
    },
    watermark: {
      immediate: true,
      async handler(val) {
        if (val) {
          await this.loadWatermarkImage()
            .then(image => {
              this.cachedWatermarkImage = image;
              return image;
            });
        }

        this.quickRender();
      },
    },
  },
  created() {
    if (!process.env.VUE_APP_IS_DEMO) {
      this.$store.dispatch('creatorLite/loadAvailableFonts');
    }
  },
  mounted() {
    this.canvas = this.$refs.canvas;
    this.ctx = this.canvas.getContext('2d');
    this.fullRender();
  },
  methods: {
    async fullRender() {
      if (!this.imageUrl) {
        return;
      }

      try {
        const promises = [];

        if (!this.cachedMainImage) {
          promises.push(this.loadImage(this.imageUrl));
        }
        else {
          promises.push(Promise.resolve(this.cachedMainImage));
        }

        if (this.logoUrl && !this.cachedLogo) {
          promises.push(this.loadImage(this.logoUrl));
        }
        else {
          promises.push(Promise.resolve(this.cachedLogo));
        }

        let [ mainImage, logoImage ] = await Promise.allSettled(promises);
        if (mainImage.status === 'fulfilled') {
          mainImage = mainImage.value;
        }
        else {
          console.error('Main image load failed:', mainImage.reason);
          mainImage = null;
        }

        if (logoImage.status === 'fulfilled') {
          logoImage = logoImage.value;
        }
        else {
          console.error('Logo image load failed:', logoImage.reason);
          logoImage = null;
        }

        this.cachedMainImage = mainImage;
        this.cachedLogo = logoImage;

        // Nothing to draw on when the image is unavailable (e.g. an expired link).
        if (!this.cachedMainImage) {
          return;
        }

        this.canvasWidth = this.cachedMainImage.width;

        this.updateCanvasDimensions();
        this.drawCanvas();
      }
      catch (error) {
        console.error('Error during rendering:', error);
      }
    },
    updateCanvasDimensions() {
      this.canvas.width = this.canvasWidth;
      this.canvas.height = this.canvasHeight;
    },
    quickRender() {
      if (this.cachedMainImage) {
        this.drawCanvas();
      }
    },
    drawCanvas() {
      if (!this.cachedMainImage) {
        return;
      }

      this.drawMainImage();

      if (this.cachedLogo || this.text) {
        this.drawGradient();

        if (this.cachedLogo) {
          this.drawLogoFromCache();
        }
      }

      // Optional Infinite.ad brand watermark; stays beneath the AI disclosure.
      if (this.watermark && this.cachedWatermarkImage) {
        this.ctx.drawImage(this.cachedWatermarkImage, 0, 0, this.canvasWidth, this.canvasWidth);
      }

      // The caption is drawn asynchronously (font + emoji loading), so the AI
      // disclosure has to be painted inside the same async tail — that is the
      // only way to guarantee it ends up as the top-most layer (EU AI Act).
      if (this.text) {
        const fontLoaderString = '0123456789?:_!@#$%^&*()[]{};\'",.<>/\\|`~'
          + 'aábcdeéfghiíjklmnoóöőpqrstuúüűvwxyz'
          + 'AÁBCDEÉFGHIÍJKLMNOÓÖŐPQRSTUÚÜŰVWXYZ';

        document.fonts.load(this.fontWeight + ' 48px ' + this.fontFamily, fontLoaderString)
          .then(async () => {
            await this.drawText();
            this.drawAiWatermark();
            this.$emit('rendered');
            return;
          })
          .catch(error => {
            console.error('Font loading error:', error);
            // The AI disclosure is mandatory even if the caption font failed.
            this.drawAiWatermark();
            this.$emit('rendered');
          });

        return;
      }

      this.drawAiWatermark();
      this.$emit('rendered');
    },
    drawMainImage() {
      const canvasAspectRatio = this.canvasWidth / this.canvasHeight;

      if (this.imageRatio === canvasAspectRatio) {
        this.ctx.drawImage(this.cachedMainImage, 0, 0, this.canvasWidth, this.canvasHeight);
      }
      else if (this.imageRatio > canvasAspectRatio) {
        const newWidth = this.cachedMainImage.height * canvasAspectRatio;
        const xOffset = (this.cachedMainImage.width - newWidth) / 2;
        this.ctx.drawImage(this.cachedMainImage, xOffset, 0, newWidth, this.cachedMainImage.height, 0, 0, this.canvasWidth, this.canvasHeight);
      }
      else {
        const newHeight = this.cachedMainImage.width / canvasAspectRatio;
        const yOffset = (this.cachedMainImage.height - newHeight) / 2;
        this.ctx.drawImage(this.cachedMainImage, 0, yOffset, this.cachedMainImage.width, newHeight, 0, 0, this.canvasWidth, this.canvasHeight);
      }
    },
    drawAiWatermark() {
      const ctx = this.ctx;
      const width = this.canvasWidth;
      const height = this.canvasHeight;

      const fontSize = Math.max(AI_WATERMARK_MIN_FONT_SIZE, Math.round(Math.min(width, height) * AI_WATERMARK_FONT_SIZE_RATIO));
      const padding = Math.round(fontSize * AI_WATERMARK_PADDING_RATIO);
      const gap = Math.round(fontSize * AI_WATERMARK_GAP_RATIO);
      const shadowOffset = Math.max(1, Math.round(fontSize * AI_WATERMARK_SHADOW_OFFSET_RATIO));
      const iconSize = Math.round(fontSize * AI_WATERMARK_ICON_HEIGHT_RATIO);

      const label = this.aiWatermarkLabel;
      const fillColor = `rgba(255, 255, 255, ${AI_WATERMARK_OPACITY})`;
      const shadowColor = `rgba(0, 0, 0, ${AI_WATERMARK_OPACITY * AI_WATERMARK_SHADOW_OPACITY_FACTOR})`;

      ctx.save();
      ctx.font = `400 ${fontSize}px "Helvetica Neue", Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      // Bottom-left row, matching the backend AiImageWatermark layout.
      const rowHeight = Math.max(iconSize, fontSize);
      const rowCenterY = height - padding - rowHeight / 2;
      const iconCenterX = padding + iconSize / 2;
      const textX = padding + iconSize + gap;

      // Shadow pass first (offset down-right), then the white fill on top.
      ctx.fillStyle = shadowColor;
      traceAiSparkles(ctx, iconCenterX + shadowOffset, rowCenterY + shadowOffset, iconSize);
      ctx.fill();
      ctx.fillText(label, textX + shadowOffset, rowCenterY + shadowOffset);

      ctx.fillStyle = fillColor;
      traceAiSparkles(ctx, iconCenterX, rowCenterY, iconSize);
      ctx.fill();
      ctx.fillText(label, textX, rowCenterY);

      ctx.restore();
    },
    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    },
    loadWatermarkImage() {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = process.env.VUE_APP_CDN_URL + '/image-renderer-watermark.png';
      });
    },
    drawGradient() {
      const gradientY = this.canvasHeight - this.gradientHeight;

      const gradient = this.ctx.createLinearGradient(
        0, gradientY,
        0, this.canvasHeight,
      );

      gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 1)');

      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, gradientY, this.canvasWidth, this.gradientHeight);
    },
    drawLogoFromCache() {
      if (!this.cachedLogo) {
        return;
      }

      const logoHeight = this.logoHeight;
      const logoWidth = (this.cachedLogo.width / this.cachedLogo.height) * logoHeight;
      const logoX = (this.canvasWidth - logoWidth) / 2;
      const logoY = this.logoYPosition;

      this.ctx.drawImage(this.cachedLogo, logoX, logoY, logoWidth, logoHeight);
    },

    async drawText() {
      this.ctx.fillStyle = 'white';
      this.ctx.textAlign = 'left';
      this.ctx.textBaseline = 'middle';

      const fontSize = 48;
      const fontStyle = this.fontWeight === '400' ? '' : this.fontWeight;
      this.ctx.font = `${fontStyle} ${fontSize}px "${this.fontFamily}", Arial`;

      // Text position: in the middle of the gradient
      let textBottomPosition = this.bottomOffset;
      if (this.cachedLogo) {
        textBottomPosition = this.logoHeight + this.bottomOffset;
      }

      const maxWidth = this.canvasWidth * 0.8;

      // Split text by line breaks first
      const paragraphs = this.text.split('\n');
      const lines = [];

      // Process each paragraph
      paragraphs.forEach(paragraph => {
        if (paragraph.trim() === '') {
          lines.push('');
          return;
        }

        const words = paragraph.split(' ');
        let currentLine = '';

        for (const word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const testWidth = this.measureTextWithEmojis(testLine, fontSize);

          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          }
          else {
            currentLine = testLine;
          }
        }

        if (currentLine) {
          lines.push(currentLine);
        }
      });

      // Draw lines
      const lineHeight = fontSize * 1.3;
      const totalHeight = lines.length * lineHeight;
      const startY = this.canvasHeight - textBottomPosition - totalHeight;

      const linePromieses = [];

      lines.forEach((line, index) => {
        const lineY = startY + index * lineHeight;
        const linePromise = this.drawLineWithEmojis(line, lineY, fontSize);
        linePromieses.push(linePromise);
      });

      await Promise.all(linePromieses);
    },

    measureTextWithEmojis(text, fontSize) {
      let totalWidth = 0;
      let lastIndex = 0;

      const emojiSize = fontSize * emojiSizeMultiplier;

      const matches = [ ...text.matchAll(emojiRegex) ];
      matches.forEach(match => {
        const beforeText = text.substring(lastIndex, match.index);
        if (beforeText) {
          totalWidth += this.ctx.measureText(beforeText).width;
        }

        totalWidth += emojiSize;
        lastIndex = match.index + match[0].length;
      });

      const remainingText = text.substring(lastIndex);
      if (remainingText) {
        totalWidth += this.ctx.measureText(remainingText).width;
      }

      return totalWidth;
    },

    async drawLineWithEmojis(text, y, fontSize) {
      if (!text.trim()) {
        return;
      }

      const emojiSize = fontSize * emojiSizeMultiplier;

      const totalWidth = this.measureTextWithEmojis(text, fontSize);
      let currentX = (this.canvasWidth - totalWidth) / 2;

      let lastIndex = 0;
      const matches = [ ...text.matchAll(emojiRegex) ];

      const emojiPromises = [];

      matches.forEach(match => {
        const beforeText = text.substring(lastIndex, match.index);
        if (beforeText) {
          this.ctx.fillText(beforeText, currentX, y);
          currentX += this.ctx.measureText(beforeText).width;
        }

        const emojiPromise = this.drawEmoji(match[0], currentX, y, emojiSize);
        emojiPromises.push(emojiPromise);

        currentX += emojiSize;
        lastIndex = match.index + match[0].length;
      });

      const remainingText = text.substring(lastIndex);
      if (remainingText) {
        this.ctx.fillText(remainingText, currentX, y);
      }

      await Promise.all(emojiPromises);
    },

    async drawEmoji(emoji, x, y, size) {
      const emojiKey = this.getEmojiKey(emoji);
      const emojiMartData = Object.values(emojiData.emojis).find(e => e.b === emojiKey || e.c === emojiKey);

      const emojiCanvas = await this.emojiExtractor.extractFromEmojiMartData(
        emojiMartData,
        'facebook',
        96,
      );

      if (emojiCanvas) {
        this.ctx.drawImage(emojiCanvas, x, y - size/2, size, size);
      }
    },

    getEmojiKey(emoji) {
      const codePoints = [ ...emoji ].map(char => char.codePointAt(0).toString(16).toUpperCase());
      return codePoints.join('-');
    },

    downloadImage() {
      if (!this.canvas) {
        console.error('Canvas not initialized');
        return;
      }

      const link = document.createElement('a');
      link.download = `infinite.ad-creator-lite-${Date.now()}.png`;
      link.href = embedAiContentMarker(this.canvas.toDataURL('image/png'));
      link.click();
    },

    getImageInBase64() {
      if (!this.canvas) {
        console.error('Canvas not initialized');
        return '';
      }

      return embedAiContentMarker(this.canvas.toDataURL('image/png'));
    },
  },
};

class EmojiExtractor {
  constructor() {
    this.spriteSheetCache = new Map();
    this.emojiCanvasCache = new Map();
  }

  async loadSpriteSheet(set = 'facebook', size = 64) {
    const spriteUrl = `https://cdn.jsdelivr.net/npm/emoji-datasource-${set}@15.0.1/img/${set}/sheets-clean/${size}.png`;

    if (this.spriteSheetCache.has(spriteUrl)) {
      return this.spriteSheetCache.get(spriteUrl);
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this.spriteSheetCache.set(spriteUrl, img);
        resolve(img);
      };
      img.onerror = reject;
      img.src = spriteUrl;
    });
  }

  async loadImage(code) {
    const url = process.env.VUE_APP_CDN_URL + '/emoji/' + code.toLowerCase() + '.png';
    if (this.spriteSheetCache.has(url)) {
      return this.spriteSheetCache.get(url);
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this.spriteSheetCache.set(url, img);
        resolve(img);
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  async extractFromEmojiMartData(emojiData, set = 'facebook', size = 64) {
    const cacheKey = `${set}-${size}-${JSON.stringify(emojiData.k)}`;

    if (this.emojiCanvasCache.has(cacheKey)) {
      return this.emojiCanvasCache.get(cacheKey);
    }

    try {
      const image = await this.loadImage(emojiData.b);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = size;
      canvas.height = size;

      ctx.drawImage(
        image,
        0, 0, canvas.width, canvas.height,
        0, 0, canvas.width, canvas.height,
      );

      this.emojiCanvasCache.set(cacheKey, canvas);
      return canvas;
    }
    catch (error) {
      console.error('Emoji load error:', error);
    }

    try {
      const emojiSize = 64;
      const spriteSheet = await this.loadSpriteSheet(set, 64);
      const [ x, y ] = emojiData.k;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = size;
      canvas.height = size;

      ctx.drawImage(
        spriteSheet,
        x * (emojiSize + 2), y * (emojiSize + 2), emojiSize, emojiSize,
        0, 0, canvas.width, canvas.height,
      );

      this.emojiCanvasCache.set(cacheKey, canvas);
      return canvas;
    }
    catch (error) {
      console.error('Canvas emoji cropping error:');
      console.error(error);
      return null;
    }
  }
}

function aiWatermarkCrc32(bytes) {
  let crc = 0xFFFFFFFF;

  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i];

    for (let bit = 0; bit < 8; bit++) {
      crc = (crc & 1) ? (crc >>> 1) ^ 0xEDB88320 : crc >>> 1;
    }
  }

  return (crc ^ 0xFFFFFFFF) >>> 0;
}

function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

function bytesToBase64(bytes) {
  let binary = '';
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
}

// Builds an uncompressed iTXt chunk (UTF-8, so the em dash / non-Latin marker
// survives) for the given keyword + text, mirroring ImageMagick's "comment".
function buildPngItxtChunk(keyword, text) {
  const keywordBytes = new TextEncoder().encode(keyword);
  const textBytes = new TextEncoder().encode(text);

  // keyword \0 compressionFlag compressionMethod langTag \0 translatedKeyword \0 text
  const data = new Uint8Array(keywordBytes.length + 5 + textBytes.length);
  let offset = 0;
  data.set(keywordBytes, offset);
  offset += keywordBytes.length;
  data[offset++] = 0; // null separator after keyword
  data[offset++] = 0; // compression flag: 0 = uncompressed
  data[offset++] = 0; // compression method
  data[offset++] = 0; // empty language tag + its null separator
  data[offset++] = 0; // empty translated keyword + its null separator
  data.set(textBytes, offset);

  const type = new TextEncoder().encode('iTXt');
  const chunk = new Uint8Array(4 + type.length + data.length + 4);
  const view = new DataView(chunk.buffer);

  view.setUint32(0, data.length); // chunk length covers the data only
  chunk.set(type, 4);
  chunk.set(data, 4 + type.length);

  const crcInput = new Uint8Array(type.length + data.length);
  crcInput.set(type, 0);
  crcInput.set(data, type.length);
  view.setUint32(4 + type.length + data.length, aiWatermarkCrc32(crcInput));

  return chunk;
}

// Embeds the EU AI Act marker into a PNG data URL's metadata, returning the
// input unchanged on any problem so exporting/publishing can never break.
function embedAiContentMarker(pngDataUrl) {
  try {
    const match = (/^data:image\/png;base64,(.+)$/).exec(pngDataUrl || '');
    if (!match) {
      return pngDataUrl;
    }

    const bytes = base64ToBytes(match[1]);
    if (PNG_SIGNATURE.some((byte, index) => bytes[index] !== byte)) {
      return pngDataUrl;
    }

    // Insert right after IHDR: signature(8) + length(4) + type(4) + data + crc(4).
    const ihdrDataLength = new DataView(bytes.buffer).getUint32(8);
    const insertAt = 8 + 4 + 4 + ihdrDataLength + 4;

    const chunk = buildPngItxtChunk('Comment', AI_WATERMARK_METADATA_MARKER);
    const out = new Uint8Array(bytes.length + chunk.length);
    out.set(bytes.subarray(0, insertAt), 0);
    out.set(chunk, insertAt);
    out.set(bytes.subarray(insertAt), insertAt + chunk.length);

    return 'data:image/png;base64,' + bytesToBase64(out);
  }
  catch (error) {
    console.error('AI watermark metadata embed failed:', error);

    return pngDataUrl;
  }
}

// Traces the "AI sparkles" glyph (a large + a small 4-point sparkle) into the
// current canvas path, echoing resources/images/ai-watermark-stars.png. The
// caller sets fillStyle and fills.
function traceAiSparkles(ctx, centerX, centerY, size) {
  const sparkle = (cx, cy, radius) => {
    const waist = radius * 0.16;

    ctx.moveTo(cx, cy - radius);
    ctx.quadraticCurveTo(cx + waist, cy - waist, cx + radius, cy);
    ctx.quadraticCurveTo(cx + waist, cy + waist, cx, cy + radius);
    ctx.quadraticCurveTo(cx - waist, cy + waist, cx - radius, cy);
    ctx.quadraticCurveTo(cx - waist, cy - waist, cx, cy - radius);
    ctx.closePath();
  };

  ctx.beginPath();
  sparkle(centerX - size * 0.08, centerY + size * 0.06, size * 0.42); // main star
  sparkle(centerX + size * 0.30, centerY - size * 0.28, size * 0.16); // accent star
}
</script>
